import {
  OrchestrationCondition,
  getConditionType,
  ConditionType,
  DayOfWeek,
  DeviceType,
  Hemisphere,
  SeasonType,
} from "../domain/orchestrationCriteria.entity";

export interface CriteriaLabelMaps {
  conditionTypeLabels: Record<ConditionType, string>;
  dayOfWeekLabels: Record<DayOfWeek, string>;
  deviceTypeLabels: Record<DeviceType, string>;
  seasonTypeLabels: Record<SeasonType, string>;
  hemisphereLabels: Record<Hemisphere, string>;
}

export function getConditionValueString(
  condition: OrchestrationCondition,
  maps: CriteriaLabelMaps
): string {
  const type = getConditionType(condition);
  if (!type) return "—";

  switch (type) {
    case ConditionType.OCCUPANCY:
      return condition.occupancy?.values?.join(", ") || "—";

    case ConditionType.CHECK_IN_DAY:
    case ConditionType.CHECK_OUT_DAY: {
      const data = condition[type];
      if (data?.activeDays?.length) {
        return data.activeDays.map(it => maps.dayOfWeekLabels[it as DayOfWeek] || it).join(", ");
      }
      return "—";
    }

    case ConditionType.LENGTH_OF_STAY:
    case ConditionType.NUMBER_OF_ROOMS: {
      const data = condition[type];
      const min = data?.min ?? 0;
      const max = data?.max;
      return `min ${min}${max !== undefined && max !== null ? `, max ${max}` : ""}`;
    }

    case ConditionType.BOOKING_WINDOW:
    case ConditionType.TRAVEL_WINDOW: {
      const windows = condition[type];
      if (!windows || windows.length === 0) {
        return "—";
      }

      if (windows.length === 1) {
        const window = windows[0];
        const parts: string[] = [];

        if (window.startDate || window.endDate) {
          parts.push(`${window.startDate || "∞"} - ${window.endDate || "∞"}`);
        }

        if (window.activeDays?.length) {
          const days = window.activeDays
            .map(it => maps.dayOfWeekLabels[it as DayOfWeek] || it)
            .join(", ");
          parts.push(days);
        }

        return parts.length ? parts.join(" | ") : "—";
      }

      return windows
        .map((window, idx) => {
          const parts: string[] = [`W${idx + 1}`];
          if (window.startDate || window.endDate) {
            parts.push(`${window.startDate || "∞"} - ${window.endDate || "∞"}`);
          }
          if (window.activeDays?.length) {
            const days = window.activeDays
              .map(it => maps.dayOfWeekLabels[it as DayOfWeek] || it)
              .join(", ");
            parts.push(days);
          }
          return parts.join(": ");
        })
        .join(" | ");
    }

    case ConditionType.DEVICE:
      return (
        condition.device?.types?.map(d => maps.deviceTypeLabels[d as DeviceType] || d).join(", ") ||
        "—"
      );

    case ConditionType.REFERRER:
      return condition.referrer?.values?.join(", ") || "—";

    case ConditionType.UTM_SOURCE:
      return condition.utmSource?.values?.join(", ") || "—";

    case ConditionType.ORIGIN:
      return condition.origin?.values?.join(", ") || "—";

    case ConditionType.SEASON: {
      const seasonLabels = condition.season?.seasons
        ?.map(s => maps.seasonTypeLabels[s as SeasonType] || s)
        .join(", ");
      const hemisphereLabel = condition.season?.hemisphere
        ? maps.hemisphereLabels[condition.season.hemisphere as Hemisphere]
        : undefined;
      const parts = [seasonLabels || "—"];
      if (hemisphereLabel) parts.push(hemisphereLabel);
      return parts.join(" | ");
    }

    default:
      return "—";
  }
}

export function conditionToStringValue(
  condition: OrchestrationCondition,
  maps: CriteriaLabelMaps
): string {
  const type = getConditionType(condition);
  if (!type) return "—";
  return `${maps.conditionTypeLabels[type]} = ${getConditionValueString(condition, maps)}`;
}
